import * as Sentry from '@sentry/react';
import { nativeLog } from 'native/utils/nativemessage';
import datadog from './datadog';
import { getCurrentLoggingContext } from './logging-context';

// Note: debug level is not shown by default in devtools console
const LEVELS = ['error', 'warn', 'log', 'info', 'debug'] as const;

const STORAGE_KEY = '_sbloglevel_';
const REALTIME_VERBOSE_STORAGE_KEY = '_sbrealtimeverbose_';

const isDev = process.env.NODE_ENV === 'development';

if (isDev && !localStorage.getItem(STORAGE_KEY)) {
  localStorage.setItem(STORAGE_KEY, LEVELS[LEVELS.length - 1]);
}

export type LogLevel = typeof LEVELS[number];

type MetalogFn = (
  type: LogLevel,
  message: string,
  object?: Record<string, any>, // serializeable
  error?: Error,
) => void;

// recursively check for Errors and extract serializable bits
const massageObj = (x: any, iteration: number): any => {
  if (iteration >= 10) {
    return '...';
  }
  if (x !== null && typeof x === 'object') {
    if (x instanceof Error) {
      return {
        name: x.name || null,
        message: x.message || null,
        stack: x.stack || null,
      };
    } else {
      const out: Record<string, any> = {};
      Object.getOwnPropertyNames(x).forEach(k => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        out[k] = massageObj((x as typeof out)[k], iteration + 1);
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return out;
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return x;
};

const isValidLevel = (level: string) => LEVELS.includes(level as LogLevel);

const getLogLevel = () => {
  const search = document.location.search.substring(1);
  const params = new URLSearchParams(search);
  const logLevel = params.get('sblog');

  // set or remove localStorage from url search
  if (logLevel && isValidLevel(logLevel)) {
    localStorage.setItem(STORAGE_KEY, logLevel);
  } else if (logLevel === 'off') {
    localStorage.removeItem(STORAGE_KEY);
  }

  // remove the sblog param/val from url search
  if (logLevel) {
    params.delete('sblog');
    const paramsStr = params.toString();
    const cleanPath = `${window.location.pathname}${paramsStr ? `?${paramsStr}` : ''}`;
    history.replaceState(null, '', cleanPath);
  }

  const storageLogLevel = localStorage.getItem(STORAGE_KEY);
  if (storageLogLevel && isValidLevel(storageLogLevel)) {
    return storageLogLevel;
  }

  return null;
};

const getLogLevelThresholdIndex = () => {
  const level = getLogLevel();
  if (level) {
    return LEVELS.indexOf(level as LogLevel);
  }
  return -1;
};

const metalog: MetalogFn = (type, message, object, error) => {
  // Log to console, if it's in the threshold
  const maxLevelIndex = getLogLevelThresholdIndex();
  if (maxLevelIndex > -1) {
    const typeLevelIndex = LEVELS.indexOf(type);
    if (typeLevelIndex <= maxLevelIndex) {
      console[type](...[message, object, error].filter(Boolean));
    }
  }

  // Log to native app whenever available
  nativeLog(type, message, object, error);

  let extra = {} as Record<string, any>;
  if (object) {
    if (typeof object === 'object' && object !== null) {
      extra = { ...object };
    }
    if (typeof object === 'string') {
      extra.message = object;
    }
  }
  if (!extra.error) {
    extra.error = error || undefined;
  } else {
    extra.error2 = error || undefined;
  }
  extra = massageObj(extra, 0);
  const ctx = { ...getCurrentLoggingContext(), ...{ extra } };

  // Datadog
  if (datadog) {
    // map 'log' to 'debug'
    const ddType = type === 'log' ? 'debug' : type;
    datadog[ddType](message, ctx);
  }

  // Sentry
  const sentryLevel =
    {
      error: Sentry.Severity.Error,
      warn: Sentry.Severity.Warning,
      info: Sentry.Severity.Info,
      log: Sentry.Severity.Log,
      debug: Sentry.Severity.Debug,
    }[type] || Sentry.Severity.Debug;
  Sentry.addBreadcrumb({
    type: 'debug',
    category: 'log',
    level: sentryLevel,
    message,
    data: ctx,
  });
};

const error = (message: string, object?: Object, error?: Error) =>
  metalog('error', message, object, error);
const warn = (message: string, object?: Object) => metalog('warn', message, object);
const _log = (message: string, object?: Object) => metalog('log', message, object);
const info = (message: string, object?: Object) => metalog('info', message, object);
const debug = (message: string, object?: Object) => metalog('debug', message, object);
const assert = (assertion: boolean, message: string, object?: Object) => {
  if (!assertion) {
    error(`assertion error: ${message}`, object);
  }
};

const log = { error, warn, log: _log, info, debug, assert };

export const makeLog = (name: string) => {
  const prefix = (msg: string) => `[${name}] ${msg}`;
  return {
    error: (msg, obj, err) => error(prefix(msg), obj, err),
    warn: (msg, obj) => warn(prefix(msg), obj),
    log: (msg, obj) => _log(prefix(msg), obj),
    info: (msg, obj) => info(prefix(msg), obj),
    debug: (msg, obj) => debug(prefix(msg), obj),
    assert: (assertion, msg, obj) => assert(assertion, prefix(msg), obj),
  } as typeof log;
};

export const isRealtimeVerboseLog = () => {
  return Boolean(localStorage.getItem(REALTIME_VERBOSE_STORAGE_KEY));
};

export default log;
